import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Brain, HeartHandshake, Sparkles, Shield, Award, Clock, BookOpen, ExternalLink, Briefcase, Activity, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import useHomepageHeadInjection from "@/hooks/useHomepageHeadInjection";
import { usePageSeo } from "@/hooks/usePageSeo";
import CityPackagesGrid from "@/components/CityPackagesGrid";
import PracticeModelInvestment from "@/components/PracticeModelInvestment";
import CalendlyInlineBooking from "@/components/CalendlyInlineBooking";
import CareCreditBanner from "@/components/CareCreditBanner";
import { RelationshipGuideCompanion } from "@/components/RelationshipGuideCompanion";

const HomePage = () => {
  const seoOverride = usePageSeo("home");
  useHomepageHeadInjection();
  const services = [
    {
      icon: HeartHandshake,
      title: "Ketamine-Assisted Psychotherapy",
      description: "Combine the neuroplasticity benefits of ketamine with guided psychotherapy for deeper, lasting healing and transformation.",
    },
    {
      icon: Activity,
      title: "Somatic Healing",
      description: "Reconnect with the body's innate wisdom to release stored trauma — an essential foundation for deep, transformative, and lasting wellness.",
    },
    {
      icon: Brain,
      title: "Trauma-Informed Care",
      description: "Specialized approach to healing trauma with compassionate, evidence-based therapeutic techniques.",
    },
    {
      icon: Sparkles,
      title: "Personalized Treatment",
      description: "Every treatment is tailored to your unique needs, history, and wellness goals for optimal results.",
    },
  ];

  const testimonials = [
    {
      quote: "Thank you so much for always being there for me and helping me through this. You really understand me.",
      author: "Client",
      role: "Therapy Client",
    },
    {
      quote: "When you find a good therapist and they just get you, you know, you start to feel better.",
      author: "Client",
      role: "Therapy Client",
    },
    {
      quote: "Wow, I've never done therapy like that before. We've only met like four times, and wow, like you're really good. You were definitely made for this.",
      author: "Client",
      role: "Therapy Client",
    },
    {
      quote: "Laura is my first therapist and she's made therapy a comfortable experience for me. I wanted a therapist who I felt would be able to truly understand the things I've been through, and I feel like Laura meets what I was looking for. Excited to continue my therapy journey with Laura as my therapist.",
      author: "Client",
      role: "Therapy Client",
    },
    {
      quote: "Everytime I leave my sessions, I'm always like yes, she's done it again! Laura did it again!",
      author: "Client",
      role: "Therapy Client",
    },
  ];

  const stateLicenses = [
    { state: "Florida", role: "Licensed Psychotherapist in FL", credential: "LCSW", number: "SW20897" },
    { state: "Maine", role: "Licensed Psychotherapist in ME", credential: "LCSW", number: "LC24279" },
    { state: "District of Columbia", role: "Licensed Psychotherapist in DC", credential: "LICSW", number: "LC200003833" },
  ];

  const getStateAbbrev = (state) => {
    const map = {
      "District of Columbia": "DC",
      "Florida": "FL",
      "Maine": "ME",
    };
    return map[state] || state;
  };

  const certifications = [
    { icon: Shield, title: "Licensed Psychotherapist", desc: "LCSW, LICSW credentials in FL, ME, and DC", licenses: stateLicenses },
    { icon: Award, title: "Trauma Certified", desc: "Certified Clinical Trauma Professional (CCTP), Certification No. 244805 — Evergreen Certified", badgeImage: "https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/i3y2ziu3_IMG_6346.jpeg" },
    { icon: Clock, title: "11 Years", desc: "Over a decade of clinical experience" },
    { icon: BookOpen, title: "Published Author", desc: "Worry Less & Beat That Anxiety Book" },
  ];

  return (
    <div className="pt-20" data-testid="home-page">
      <SEO
        title="Ketamine-Assisted Psychotherapy"
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        description={seoOverride?.meta_description || "Rediscover hope with compassionate, trauma-informed Ketamine-Assisted Psychotherapy. Laura A. Jeanis, LCSW, LICSW, CCTP, CEO — licensed in FL, ME & DC."}
        imageAlt={seoOverride?.image_alt}
        pathname="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1718436171027-f1cc75d0800b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwyfHxjYWxtaW5nJTIwbmF0dXJlJTIwbGFuZHNjYXBlJTIwZ2VudGxlJTIwbGlnaHR8ZW58MHx8fHwxNzcwOTE3NTE1fDA&ixlib=rb-4.1.0&q=85"
            alt="Tranquil nature landscape"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
              Online Ketamine-Assisted Therapy (KAP) For Women in{" "}
              <span className="italic text-primary">Cumberland &amp; Portland Maine, Brickell Miami Florida, &amp; Washington DC</span>
            </h1>
            <div className="mt-6 space-y-4">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground">
                Supporting Hyper-Independent Women Who Are Tired Of Not Feeling Safe In Relationships Through The Power Of Evidence-Based Ketamine-Assisted Therapy
              </h2>

              <blockquote
                className="mt-6 relative pl-6 border-l-4 border-primary/60 bg-primary/5 rounded-r-2xl py-5 pr-6"
                data-testid="homepage-impact-statement"
              >
                <p className="font-serif text-lg sm:text-xl md:text-2xl italic font-medium text-foreground leading-snug tracking-tight">
                  &ldquo;I help high-achieving, hyper-independent women use
                  Ketamine-Assisted Psychotherapy to rewire their nervous
                  system for safety — so they can stop believing it&rsquo;s unsafe
                  to let people in and finally build{" "}
                  <span className="text-primary">connected, lasting relationships.</span>&rdquo;
                </p>
                <footer className="mt-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  — Laura A. Jeanis, LCSW, LICSW, CCTP
                </footer>
              </blockquote>

              <div className="mt-2 space-y-4" data-testid="hyper-independent-intro">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  I know you&rsquo;re used to carrying it all, but what&rsquo;s the cost? You feel burnt out, angry, and resentful because being hyper-independent means you can only trust yourself.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  There is usually a deeper root (trauma) which requires deeper healing. Using KAP, we can create new neural pathways in the brain to help you end the cycle and feel safe in your relationships.
                </p>
                <p className="text-lg font-medium italic text-primary leading-relaxed">
                  I&rsquo;ve been there — and now I&rsquo;m on the other side.
                </p>
              </div>

              <div className="pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg"
                    alt="Laura A. Jeanis, LCSW, LICSW, CCTP — Founder of Divine Love Wellness Enterprises"
                    className="h-16 w-16 rounded-full object-cover border-2 border-primary/20 shadow-soft flex-shrink-0"
                    data-testid="meet-founder-portrait"
                  />
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                    Meet The Founder
                  </h3>
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  For most of my life, I was hyper-independent and never felt safe in my relationships. I couldn&rsquo;t ask for help, I couldn&rsquo;t let anyone in, and I definitely couldn&rsquo;t let anyone get too close.
                </p>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                After my KAP journey, my friendships and relationships are peaceful, I can ask for help, I feel less alone, and I can finally receive love.
              </p>

              <div className="pt-6" data-testid="what-is-kap-section">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mb-4">
                  What is KAP?
                </h3>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    KAP is another term for Ketamine-Assisted Psychotherapy. Ketamine is a legal psychedelic that can be taken in many forms; I utilize the tablet form for my telemedicine sessions.
                  </p>
                  <p>
                    KAP works with the NMDA receptors in the brain, which enhances neuroplasticity. This is why KAP helps you create new neural pathways that help your brain solve complex challenges in new ways that it never could before.
                  </p>
                  <p>
                    You can expect to experience enhanced therapeutic breakthroughs within the first dosing session. After the dosing session, we do the psychedelic integration session to maximize the benefits of the ketamine.
                  </p>
                  <p>
                    KAP goes to the root of the issues that caused you to be a hyper-independent woman — it helps you effectively process the things that kept you in this loop. The changes continue on even long after you&rsquo;ve completed your journey with me.
                  </p>
                  <p className="italic text-primary font-medium">
                    After many years in talk therapy, I thought I would be in therapy my whole life. Although each case is unique, after KAP came into my life, I haven&rsquo;t been in therapy since.
                  </p>
                </div>
              </div>

              <div className="pt-6" data-testid="specialize-section">
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mb-4">
                  I specialize in helping hyper-independent women who:
                </h3>
                <ul className="space-y-3">
                  {[
                    "Struggle to ask for help, even when they really need it",
                    "Experience sudden outbursts of anger over minor inconveniences",
                    "Feel that opening up to a partner is dangerous, because they’ll lose their freedom and sense of self",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-lg text-muted-foreground leading-relaxed">
                      <span className="mt-2.5 h-2 w-2 rounded-full bg-accent flex-shrink-0"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed pt-2">
                If you&rsquo;re tired of carrying everything on your own, and tired of not being able to let anyone in, I&rsquo;d love to help.
              </p>
              <p className="text-lg font-medium text-foreground leading-relaxed">
                Book a free 40-minute consultation.
              </p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <a href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="hero-cta-primary"
                >
                  Book Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/services">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg border-primary/20 text-primary hover:bg-primary/5"
                  data-testid="hero-cta-secondary"
                >
                  Explore Treatments
                </Button>
              </Link>
              <a href="https://a.co/d/07TEq4qE" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="rounded-full px-8 py-6 text-lg btn-scale shadow-soft bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="hero-cta-amazon"
                >
                  <BookOpen className="mr-2 h-5 w-5" />
                  Get on Amazon
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Community Guide — relationship anxiety gift */}
      <RelationshipGuideCompanion />

      {/* City-Specific Packages Nudge */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-white" data-testid="home-city-packages">
        <div className="max-w-6xl mx-auto">
          <CityPackagesGrid
            eyebrow="Where do you live?"
            heading="See the offering built for your state"
            subheading="I'm licensed in Florida, Maine, & DC — pick your state to see the intensive designed for you."
            testIdPrefix="home-city"
            dcTarget="specialty"
            showGroupImmersion={true}
          />
        </div>
      </section>

      {/* Practice Model & Investment */}
      <PracticeModelInvestment background="bg-secondary/30" />

      {/* CareCredit Financing Banner — paired with Practice Model & Investment */}
      <CareCreditBanner />

      {/* Certifications Bar */}
      <section className="bg-secondary py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start gap-4"
              >
                {cert.badgeImage ? (
                  <img
                    src={cert.badgeImage}
                    alt="Evergreen Certified — Certified Clinical Trauma Professional (CCTP)"
                    className="h-16 w-16 rounded-full object-cover ring-2 ring-accent/40 shadow-soft flex-shrink-0"
                    loading="lazy"
                    data-testid="home-cctp-badge"
                  />
                ) : (
                  <div className="p-3 rounded-full bg-accent/30">
                    <cert.icon className="h-6 w-6 text-primary" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-foreground">{cert.title}</h3>
                  <p className="text-sm text-muted-foreground">{cert.desc}</p>
                  {cert.licenses && (
                    <ul className="mt-3 space-y-2" data-testid="state-licenses-list">
                      {cert.licenses.map((lic) => (
                        <li key={lic.state} className="text-xs text-muted-foreground" data-testid={`state-license-${lic.state.toLowerCase().replace(/\s+/g, '-')}`}>
                          <span className="font-medium text-foreground">{lic.role}</span>
                          <br />
                          {lic.credential} credentials in {getStateAbbrev(lic.state)}, License Number: <span className="font-medium">{lic.number}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional Verification */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 pt-8 border-t border-border/40"
            data-testid="home-additional-verification"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Also Verified By
            </span>
            <a
              href="https://www.psychologytoday.com/profile/1844694"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center transition-opacity hover:opacity-80"
              aria-label="View Laura A. Jeanis verified profile on Psychology Today"
            >
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/semz0occ_IMG_4071.jpeg"
                alt="Verified by Psychology Today"
                className="h-12 w-auto rounded-md shadow-sm"
                loading="lazy"
                data-testid="home-psychology-today-badge"
              />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Our Treatment Approach
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Evidence-based ketamine therapy combined with compassionate care 
              for transformative results.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 h-full bg-white border-border/40 hover:border-primary/30 transition-colors duration-300 shadow-sm hover:shadow-soft group rounded-2xl">
                  <div className="p-3 rounded-full bg-secondary w-fit mb-6 group-hover:bg-accent/30 transition-colors">
                    <service.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-3">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <Button 
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-primary/20 text-primary hover:bg-primary/5"
                data-testid="services-cta"
              >
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Book Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/50 relative">
        <div className="subtle-glow absolute inset-0" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Book Cover */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <a 
                href="https://a.co/d/07TEq4qE" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block transform hover:scale-105 transition-transform duration-300"
                data-testid="home-book-cover-link"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_therapy-solutions-1/artifacts/unwyel7v_IMG_2280.jpeg"
                  alt="Worry Less & Beat That Anxiety! by Laura A. Jeanis"
                  className="w-full max-w-xs rounded-2xl shadow-hover"
                />
              </a>
            </motion.div>

            {/* Book Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5 text-accent" />
                <span className="text-accent font-medium">New Book Available</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
                Worry Less & Beat That Anxiety!
              </h2>
              <div className="mt-6 space-y-3" data-testid="book-is-this-for-you">
                <p className="text-muted-foreground leading-relaxed italic border-l-2 border-accent/40 pl-4">
                  "This book is for you if you're an empathic woman who absorbs everyone else's energy and needs a way to come home to yourself, calm your nervous system, and trust your intuition again."
                </p>
                <p className="text-muted-foreground leading-relaxed italic border-l-2 border-accent/40 pl-4">
                  "This book is for you if you've tried meditation apps, journals, and self-help books, but still feel like your anxiety has a grip on your spirit as much as your mind."
                </p>
                <p className="text-muted-foreground leading-relaxed italic border-l-2 border-accent/40 pl-4">
                  "This book is for you if you long for a workbook that feels like a gentle guide and a wise friend, helping you worry less and trust more — in yourself, in your path, and in something greater."
                </p>
              </div>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                If you are tired of anxiety, racing thoughts, and triggers that you can't 
                pinpoint, then get this book!
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                An 11-week journey to go deep within, learn about anxiety, identify triggers, 
                and take your power back.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://a.co/d/07TEq4qE" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg" 
                    className="rounded-full px-8 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="home-buy-book-btn"
                  >
                    <ExternalLink className="mr-2 h-5 w-5" />
                    Get on Amazon
                  </Button>
                </a>
                <Link to="/book">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg border-primary/20 text-primary hover:bg-primary/5"
                    data-testid="home-book-learn-more"
                  >
                    Learn More
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 md:py-32 px-6 md:px-12 relative">
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Stories of Healing
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Real experiences from clients who found hope through our therapy programs.
            </p>
          </motion.div>

          {/* Diamond Layout */}
          <div className="flex flex-col items-center gap-6">
            {/* Top - 1 card */}
            <div className="w-full max-w-md">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 bg-white/80 backdrop-blur-sm border-0 rounded-2xl relative">
                  <div className="absolute top-4 left-6 text-5xl font-serif text-accent/50">"</div>
                  <blockquote className="relative z-10 pt-6">
                    <p className="text-foreground leading-relaxed italic text-sm">
                      {testimonials[0].quote}
                    </p>
                    <footer className="mt-4 pt-4 border-t border-border/30">
                      <cite className="not-italic">
                        <span className="font-semibold text-foreground block">{testimonials[0].author}</span>
                        <span className="text-sm text-muted-foreground">{testimonials[0].role}</span>
                      </cite>
                    </footer>
                  </blockquote>
                </Card>
              </motion.div>
            </div>

            {/* Middle Row - 2 cards */}
            <div className="w-full flex flex-col md:flex-row justify-center gap-6">
              {[1, 2].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="w-full md:w-[380px]"
                >
                  <Card className="p-8 h-full bg-white/80 backdrop-blur-sm border-0 rounded-2xl relative">
                    <div className="absolute top-4 left-6 text-5xl font-serif text-accent/50">"</div>
                    <blockquote className="relative z-10 pt-6">
                      <p className="text-foreground leading-relaxed italic text-sm">
                        {testimonials[i].quote}
                      </p>
                      <footer className="mt-4 pt-4 border-t border-border/30">
                        <cite className="not-italic">
                          <span className="font-semibold text-foreground block">{testimonials[i].author}</span>
                          <span className="text-sm text-muted-foreground">{testimonials[i].role}</span>
                        </cite>
                      </footer>
                    </blockquote>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Bottom Row - 2 cards */}
            <div className="w-full flex flex-col md:flex-row justify-center gap-6">
              {[3, 4].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="w-full md:w-[380px]"
                >
                  <Card className="p-8 h-full bg-white/80 backdrop-blur-sm border-0 rounded-2xl relative">
                    <div className="absolute top-4 left-6 text-5xl font-serif text-accent/50">"</div>
                    <blockquote className="relative z-10 pt-6">
                      <p className="text-foreground leading-relaxed italic text-sm">
                        {testimonials[i].quote}
                      </p>
                      <footer className="mt-4 pt-4 border-t border-border/30">
                        <cite className="not-italic">
                          <span className="font-semibold text-foreground block">{testimonials[i].author}</span>
                          <span className="text-sm text-muted-foreground">{testimonials[i].role}</span>
                        </cite>
                      </footer>
                    </blockquote>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Inline Calendly Booking Widget */}
      <CalendlyInlineBooking background="bg-secondary/30" />

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Ready to Start Your Healing Journey?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Take the first step toward a brighter tomorrow. Schedule a free consultation 
              to learn if ketamine therapy is right for you.
            </p>
            <div className="mt-10">
              <a href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="rounded-full px-10 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="cta-bottom"
                >
                  Schedule Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
